import type { CanDoAction } from '@ardoq/access-control';

type SectionDefinition = {
  path: `/${string}`;
  entryPath: `/${string}`;
  requiredPermission: CanDoAction;
  title: string;
  i18nKey: string;
  unauthorizedMessage: string;
};

const NO_ADMIN_ACCESS_MESSAGE =
  'You do not have access to edit the workspace. Please contact your administrator if you need access to this feature.';

export const workspaceManagerSections = {
  settings: {
    path: '/settings',
    entryPath: '/settings',
    requiredPermission: 'workspace/admin',
    title: 'Workspace settings',
    i18nKey: 'workspaceManager:sidebarItemSettings',
    unauthorizedMessage: NO_ADMIN_ACCESS_MESSAGE,
  },
  history: {
    path: '/history',
    entryPath: '/history',
    requiredPermission: 'workspace/read',
    title: 'History',
    i18nKey: 'workspaceManager:sidebarItemHistory',
    unauthorizedMessage:
      'You do not have access to the workspace history. Please contact your administrator if you need access to this feature.',
  },
  assets: {
    path: '/assets',
    entryPath: '/assets',
    requiredPermission: 'workspace/read',
    title: 'Linked assets',
    i18nKey: 'workspaceManager:sidebarItemAssets',
    unauthorizedMessage:
      'You do not have access to the linked assets of the workspace. Please contact your administrator if you need access to this feature.',
  },
  'workspace-documents': {
    path: '/workspace-documents',
    entryPath: '/workspace-documents',
    requiredPermission: 'workspace/edit',
    title: 'Workspace documents',
    i18nKey: 'workspaceManager:sidebarItemDocuments',
    unauthorizedMessage:
      'You do not have access to workspace documents. Please contact your administrator if you need access to this feature.',
  },
  metamodel: {
    path: '/metamodel',
    entryPath: '/metamodel/component-types',
    requiredPermission: 'workspace/admin',
    title: 'Metamodel',
    i18nKey: 'workspaceManager:sidebarItemMetamodel',
    unauthorizedMessage: NO_ADMIN_ACCESS_MESSAGE,
  },
  fields: {
    path: '/fields',
    entryPath: '/fields',
    requiredPermission: 'workspace/edit_fields',
    title: 'Calculated fields',
    i18nKey: 'workspaceManager:sidebarItemFields',
    unauthorizedMessage:
      'You do not have access to edit workspace fields. Please contact your administrator if you need access to this feature.',
  },
  permissions: {
    path: '/permissions',
    entryPath: '/permissions',
    requiredPermission: 'workspace/admin',
    title: 'Permissions',
    i18nKey: 'workspaceManager:sidebarItemPermissions',
    unauthorizedMessage: NO_ADMIN_ACCESS_MESSAGE,
  },
  'confidential-fields': {
    path: '/confidential-fields',
    entryPath: '/confidential-fields',
    requiredPermission: 'workspace/admin',
    title: 'Confidential fields',
    i18nKey: 'workspaceManager:sidebarItemConfidentialFields',
    unauthorizedMessage: NO_ADMIN_ACCESS_MESSAGE,
  },
} as const satisfies Record<string, SectionDefinition>;

export type WorkspaceManagerSectionId = keyof typeof workspaceManagerSections;

export type WorkspaceManagerSection =
  (typeof workspaceManagerSections)[WorkspaceManagerSectionId];

type SectionGroupDefinition = {
  title: string;
  i18nKey: string;
  sectionIds: readonly WorkspaceManagerSectionId[];
};

export const workspaceManagerSectionGroups = [
  {
    title: 'Overview',
    i18nKey: 'workspaceManager:sidebarGroupOverview',
    sectionIds: ['settings', 'history', 'assets', 'workspace-documents'],
  },
  {
    title: 'Workspace metamodel',
    i18nKey: 'workspaceManager:sidebarGroupMetamodel',
    sectionIds: ['metamodel', 'fields'],
  },
  {
    title: 'Access control',
    i18nKey: 'workspaceManager:sidebarGroupAccessControl',
    sectionIds: ['permissions', 'confidential-fields'],
  },
] as const satisfies SectionGroupDefinition[];

export type WorkspaceManagerSectionGroup =
  (typeof workspaceManagerSectionGroups)[number];

export const workspaceManagerSectionIds: WorkspaceManagerSectionId[] =
  workspaceManagerSectionGroups.flatMap(group => [...group.sectionIds]);
